/**
 * @fileoverview "What are you seeing?": the first question of the Plant
 * Doctor's window, before the "What you found" box (owner, 2026-10-15, after
 * a check-up called gray aphids flea beetles: "Maybe start with a question
 * of… do you see any bugs?").
 *
 * The gardener taps what they see (one or more: bugs on it, holes or chewed
 * leaves, spots or patches, yellowing or wilting, something else / not
 * sure); with bugs, two more: the bugs' colour and what they are doing (one
 * each). The choices and every word of them come from the server
 * (GET /plant-doctor/seeing-choices, getSeeingChoices), in the gardener's
 * language; the app only shows them and sends back the ids with the
 * check-up, where they go to the model as facts.
 *
 * Choices look and act like the follow-up questions' answers: outlined
 * buttons 44px tall, filled in the theme's primary when chosen
 * (aria-pressed). A missing answer after Take a look is said in red under
 * its question, as the "What you found" box does.
 *
 * Loading: a small spinner. A failure on our side (D31) says the standard
 * sentence (errorMessage, reported to Sentry) and the look can still be
 * taken without these answers (seeingComplete treats no choices as done).
 * Cached per language (SEEING_CHOICES_QUERY_KEY) for the session.
 *
 * Where: PlantDoctor's window. Words (en/es): the choices from the server;
 * plants.diagnose.seeingRequired, seeingBugsRequired, failed.
 *
 * @module DoctorSeeing
 */

import React from 'react';
import { Check } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getSeeingChoices, SEEING_BUGS_ID, type ReportedSeeing, type SeeingQuestion } from '@/shared/api/diagnose';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import Spinner from '@/shared/components/ui/Spinner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { cn } from '@/lib/utils';

/** The react-query key of the choices, by language. */
export const SEEING_CHOICES_QUERY_KEY = ['plant-doctor', 'seeing-choices'] as const;

/** The choices change only with a release: kept for the session. Unit: ms. */
const SEEING_CHOICES_STALE_MS = Infinity;

/** What the gardener has tapped so far; empty to start. */
export const NO_SEEING: ReportedSeeing = { what: [], bugColour: null, bugHabit: null };

/**
 * Whether the answers are complete enough to look: at least one thing seen,
 * and with bugs, their colour and what they are doing. With no choices
 * loaded (a failure, or still loading), nothing is required.
 *
 * @param seeing - What was tapped.
 * @param loaded - Whether the choices were shown.
 * @returns The two checks: anything seen, and the bug questions answered.
 */
export function seeingComplete(seeing: ReportedSeeing, loaded: boolean): { what: boolean; bugs: boolean } {
  if (!loaded) return { what: true, bugs: true };
  const bugs = seeing.what.includes(SEEING_BUGS_ID);
  return { what: seeing.what.length > 0, bugs: !bugs || (Boolean(seeing.bugColour) && Boolean(seeing.bugHabit)) };
}

/**
 * What the server is sent: only the bug answers with bugs, null with nothing tapped.
 *
 * @param seeing - What was tapped.
 * @returns The answers, or null.
 */
export function seeingToSend(seeing: ReportedSeeing): ReportedSeeing | null {
  if (seeing.what.length === 0) return null;
  const bugs = seeing.what.includes(SEEING_BUGS_ID);
  return { what: seeing.what, bugColour: bugs ? seeing.bugColour ?? null : null, bugHabit: bugs ? seeing.bugHabit ?? null : null };
}

/** What ChoiceRow is given. */
interface ChoiceRowProps {
  /** The question: its heading and choices. */
  question: SeeingQuestion;
  /** The ids chosen. */
  chosen: string[];
  /** Taps one choice. */
  onTap: (id: string) => void;
  /** Said in red under the question when it is missing an answer; null otherwise. */
  missing: string | null;
}

/**
 * One question: its heading as a legend, and its choices as buttons.
 *
 * @param props - ChoiceRowProps.
 */
const ChoiceRow: React.FC<ChoiceRowProps> = ({ question, chosen, onTap, missing }) => (
  <fieldset className="space-y-2">
    <legend className="mb-2 text-sm font-medium">{question.heading}</legend>
    <div className="flex flex-wrap gap-2">
      {question.choices.map((choice) => {
        const on = chosen.includes(choice.id);
        return (
          <button
            key={choice.id}
            type="button"
            aria-pressed={on}
            onClick={() => onTap(choice.id)}
            className={cn(
              'inline-flex min-h-11 items-center gap-1.5 rounded-md border-2 px-3 text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:translate-y-px',
              on
                ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                : cn('bg-background text-foreground shadow-sm hover:border-primary hover:bg-muted', missing ? 'border-destructive' : 'border-foreground/40'),
            )}
          >
            {on && <Check className="h-4 w-4" aria-hidden="true" />}
            {choice.label}
          </button>
        );
      })}
    </div>
    {missing && <p className="text-sm font-medium text-destructive">{missing}</p>}
  </fieldset>
);

/** What DoctorSeeing is given. */
interface DoctorSeeingProps {
  /** What was tapped so far. */
  value: ReportedSeeing;
  /** Keeps a change. */
  onChange: (next: ReportedSeeing) => void;
  /** True after Take a look was pressed: a missing answer is said. */
  tried: boolean;
  /** Tells the window whether the choices are shown (so they are required). */
  onLoaded: (loaded: boolean) => void;
}

/**
 * "What are you seeing?", and with bugs their colour and what they are doing.
 *
 * @param props - DoctorSeeingProps.
 */
export const DoctorSeeing: React.FC<DoctorSeeingProps> = ({ value, onChange, tried, onLoaded }) => {
  const { reactTranslate: t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
  const { data, isLoading, error } = useQuery({
    queryKey: [...SEEING_CHOICES_QUERY_KEY, lang],
    queryFn: () => getSeeingChoices(lang),
    staleTime: SEEING_CHOICES_STALE_MS,
  });
  const loaded = Boolean(data);
  React.useEffect(() => { onLoaded(loaded); }, [loaded, onLoaded]);

  if (isLoading) return <div className="flex justify-center py-2"><Spinner size={20} color="hsl(var(--primary))" /></div>;
  if (error || !data) {
    return error ? <p role="alert" className="text-sm text-destructive"><FailureText text={errorMessage(error, t('plants.diagnose.failed'))} /></p> : null;
  }

  const bugs = value.what.includes(SEEING_BUGS_ID);
  const complete = seeingComplete(value, true);
  /**
   * Taps a "what" choice: on or off; turning bugs off forgets the bug answers.
   * @param id - The choice.
   */
  const tapWhat = (id: string): void => {
    const what = value.what.includes(id) ? value.what.filter((w) => w !== id) : [...value.what, id];
    const keepBugs = what.includes(SEEING_BUGS_ID);
    onChange({ what, bugColour: keepBugs ? value.bugColour : null, bugHabit: keepBugs ? value.bugHabit : null });
  };

  return (
    <div className="space-y-4">
      <ChoiceRow
        question={data.what}
        chosen={value.what}
        onTap={tapWhat}
        missing={tried && !complete.what ? t('plants.diagnose.seeingRequired') : null}
      />
      {bugs && (
        <>
          <ChoiceRow
            question={data.colour}
            chosen={value.bugColour ? [value.bugColour] : []}
            onTap={(id) => onChange({ ...value, bugColour: id })}
            missing={tried && !value.bugColour ? t('plants.diagnose.seeingBugsRequired') : null}
          />
          <ChoiceRow
            question={data.habit}
            chosen={value.bugHabit ? [value.bugHabit] : []}
            onTap={(id) => onChange({ ...value, bugHabit: id })}
            missing={tried && !value.bugHabit ? t('plants.diagnose.seeingBugsRequired') : null}
          />
        </>
      )}
    </div>
  );
};
